import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { debounceTime, exhaustMap, map, of, tap } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';

import {
  EffectsBase,
  SocketActions,
  StorageService,
} from '@ps/core-data-access';

import { SsoService } from '../../services/sso.service';
import { AuthSessionActions } from '../auth-session/auth-session.actions';
import { AuthSsoActions } from './auth-sso.actions';
import { selectSsoEmail } from './auth-sso.selectors';
import { SsoErrors } from './auth-sso.errors';

@Injectable()
export class AuthSsoEffects extends EffectsBase {
  private readonly _document = inject(DOCUMENT);
  private readonly _storage = inject(StorageService);
  private readonly _store = inject(Store);
  private readonly _actions$ = inject(Actions);
  private readonly _ssoService = inject(SsoService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _errors = new SsoErrors();

  public checkEmail$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthSsoActions.checkEmail),
      exhaustMap(({ email }) =>
        this._ssoService.checkContext(email).pipe(
          map(({ config }) =>
            config.match({
              some: (oidcConfig) =>
                AuthSsoActions.checkEmailSuccess({ config: oidcConfig }),
              none: () =>
                AuthSsoActions.checkEmailFailure({
                  error: this._errors.notAuthorized(),
                }),
            }),
          ),
          this.psCatchError(
            () =>
              of(
                AuthSsoActions.checkEmailFailure({
                  error: this._errors.serviceUnavailable(),
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public redirectToProvider$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AuthSsoActions.checkEmailSuccess),
        concatLatestFrom(() => this._store.select(selectSsoEmail)),
        takeUntilDestroyed(this._destroyRef),
        tap(([{ config }, email]) => {
          this._storage.set('ps-sso-email', email);
          this._document.location.href = this._ssoService.buildOidcAuthUrl(
            config,
            email,
          );
        }),
      ),
    { dispatch: false },
  );

  public exchangeCode$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthSsoActions.exchangeCode),
      exhaustMap(({ code }) =>
        this._ssoService.exchangeCode(code).pipe(
          map((user) => AuthSessionActions.loginSuccess({ user })),
          this.psCatchError(
            () =>
              of(
                AuthSsoActions.exchangeCodeFailure({
                  error: this._errors.syncFailed(),
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public ssoLogoutRedirect$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AuthSessionActions.logoutSuccess),
        tap(({ isSso }) => {
          if (!isSso) return;
          this._document.location.assign(
            this._ssoService.buildOidcLogoutUrl(this._document.location.origin),
          );
        }),
      ),
    { dispatch: false },
  );

  public readonly connectOnAuth$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        AuthSessionActions.loginSuccess,
        AuthSessionActions.hydrateSuccess,
        AuthSessionActions.rehydrate,
      ),
      debounceTime(100),
      map(({ user }) => SocketActions.connect({ token: user.token })),
    ),
  );

  public readonly disconnectOnLogout$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthSessionActions.clearState),
      map(() => SocketActions.disconnect()),
    ),
  );
}
